import React, { createContext, useContext } from 'react';
import type { Context, ReactElement, ReactNode } from 'react';

import { RendererCssClassName } from '@atlaskit/renderer/consts';

// Cells never start a light subtree, so they take no observer ref.
type CellProps = {
	background?: string;
	children?: ReactNode;
	colspan?: number;
	rowspan?: number;
};

const cellStyle = (background?: string) =>
	background ? { backgroundColor: background } : undefined;

export const TableCell = ({ background, children, colspan, rowspan }: CellProps): ReactElement => (
	<td
		colSpan={colspan}
		data-cell-background={background}
		rowSpan={rowspan}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Cell background comes from ADF.
		style={cellStyle(background)}
	>
		{children}
	</td>
);

/** What cells need from their table, which knows each row's position. */
type LightTableContextValue = {
	isNumberColumnEnabled: boolean;
	isSortableHeaderRow: boolean;
};

export const LightTableContext: Context<LightTableContextValue> =
	createContext<LightTableContextValue>({
		isNumberColumnEnabled: false,
		isSortableHeaderRow: false,
	});

// Platform renders a sortable header row's content in a padded, bordered column div. The same
// classes give light header cells their geometry; the sort icon takes no space, so it is left out.
export const TableHeader = ({
	background,
	children,
	colspan,
	rowspan,
}: CellProps): ReactElement => {
	const isSortable = useContext(LightTableContext).isSortableHeaderRow;
	return (
		<th
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's sortable column styles.
			className={isSortable ? RendererCssClassName.SORTABLE_COLUMN_WRAPPER : undefined}
			colSpan={colspan}
			data-cell-background={background}
			rowSpan={rowspan}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Cell background comes from ADF.
			style={cellStyle(background)}
		>
			{isSortable ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's sortable column styles.
				<div className={RendererCssClassName.SORTABLE_COLUMN}>{children}</div>
			) : (
				children
			)}
		</th>
	);
};

// Keeps the number column's geometry; the numbers appear when the table upgrades.
export const NumberColumnCell = (): ReactElement | null =>
	useContext(LightTableContext).isNumberColumnEnabled ? (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's number column styles.
		<td className={RendererCssClassName.NUMBER_COLUMN} data-reaches-left />
	) : null;
